import { FC, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useForm, yupResolver } from '@mantine/form';
import { Flex, Space, Text } from '@mantine/core';
import { AlertCircle } from '@geist-ui/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { NativeSelect, Select, TextInput } from '@common/components';
import { IOnboardingFormValues } from '@common/types/types';
import { AuthFormHoc } from 'src/hoc';
import { useAlert, useFormFocus } from '@common/hooks';
import { validationPattern } from '@common/helpers';
// import { industryOptions, industryOptionsNative } from 'src/constants/industryOptions';
import { PATHS } from 'src/constants';
import {
  setIsAuthorizeAction,
  useAppDispatch,
  addSiteList,
  currentAction,
  setUserIdAction,
  updateCurrent,
  createCompany,
  useAppSelector,
  createClientOrganization,
  // updateInfosIndustry,
} from 'src/store';
import { Auth } from 'aws-amplify';

import styles from './styles.module.scss';
import { setOnboardingStatus } from 'src/helpers/localStorageHelper';
import { shallowEqual, useOs } from '@mantine/hooks';

const initialValues: IOnboardingFormValues = {
  siteUrl: '',
  companyName: '',
  // industryField: '',
};

const validationFormSchema = yup.object().shape({
  siteUrl: validationPattern.websiteUrl,
  companyName: validationPattern.companyName,
  // industryField: validationPattern.industryField,
});

const OnboardingForm: FC = (): JSX.Element => {
  const {
    data
  } = useAppSelector((state) => state.user, shallowEqual);
  const os = useOs();
  const [t] = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { showAlert } = useAlert();

  // const { getInfoIndustry } = useAppSelector((state) => state.organization, shallowEqual);
  const form = useForm({
    initialValues: initialValues,
    validate: yupResolver(validationFormSchema),
    transformValues: (values) => {
      if (!values.siteUrl) {
        return values;
      }

      let newUrl;
      const urlPattern = /^((http|https):\/\/)/;

      if (!urlPattern.test(values.siteUrl)) {
        newUrl = 'https://' + values.siteUrl;
      }
      return {
        siteUrl: newUrl || values.siteUrl,
        companyName: values.companyName,
        // industryField: values.industryField,
      };
    },
  });
  const { setNeedFocus } = useFormFocus({ formErrors: form.errors });

  useEffect(() => {
    const getSession = async (): Promise<void> => {
      const data = await Auth.currentSession();
      const idToken: string = data.getIdToken().getJwtToken();
      dispatch(setUserIdAction(idToken));
      dispatch(currentAction());
    };

    getSession();
  }, []);

  const [systemError, setSystemError] = useState<null | string>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (systemError) {
      errorRef.current?.setAttribute('tabindex', '0');
      errorRef.current?.focus();
    }
  }, [systemError]);

  const submitCallback = (): void => {

    // console.log(`submit callback`);

    setOnboardingStatus('true');
    dispatch(setIsAuthorizeAction(true));
    dispatch(
      updateCurrent({ data: { isNewsletterNotificationsEnabled: localStorage.getItem('newsletterSub') === 'true' } }),
    );
    navigate(PATHS.MAIN);
  };

  const onSubmitHandler = async (formData: IOnboardingFormValues): Promise<void> => {
    // console.log(`testSubmitHandler called `);
    // console.log(formData);
    setNeedFocus(true);
    setIsLoading(true);
    setSystemError(null);
    try {
      location.state = {};
      dispatch(createClientOrganization({
        tenant_id: data?.tenantId,
        name: formData.companyName,
        addSiteToCompany: (company: any) => {
          showAlert({
            id: 'create-company',
            title: `Create Company`,
            message: `${formData.companyName} has been created`,
            variant: 'success',
            autoClose: 3000,
          });

          const values = { websiteUrl: formData.siteUrl }
          dispatch(addSiteList({ values, companyId: company?.id, isAccountPage : true, submitCallback: submitCallback }));
        },
        errorCallback: (error: string) => {
          onErrorHandler()
          showAlert({
            id: 'error',
            title: 'Enable To Create Company',
            message: error,
            variant: 'error',
            autoClose: 3000,
          });
          setNeedFocus(true)
        }

      }))
    } catch (error) {
      if (error instanceof Error) {
        setSystemError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const firstLetterToLowerCaseInSentence = (sentence: string): string =>
    sentence.charAt(0).toLowerCase() + sentence.slice(1);

  const onErrorHandler = (): void => {
    setNeedFocus(true);
    setSystemError(null);
  };

  return (
    <AuthFormHoc
      title={t('letsStart')}
      description={
        <>
          <p>{t('onboardingDescription')}</p>
        </>
      }
      form={form}
      submitText={`${t('next')}`}
      onSubmitHandler={onSubmitHandler}
      onErrorHandler={onErrorHandler}
      isLoading={isLoading}>
      <>
        {systemError && (
          <Flex ref={errorRef}>
            <Text color={'#a80000'}>{systemError}</Text>
          </Flex>
        )}
        <TextInput
          label={`${t('siteUrl')}`}
          placeholder={`${t('enter')} ${firstLetterToLowerCaseInSentence(t('siteUrl'))}`}
          ariaLabel={`${t('siteUrl')}`}
          icon={<AlertCircle size="1.5rem" />}
          withAsterisk
          {...form.getInputProps('siteUrl')}
        />
        <Space h={'md'} />
        <TextInput
          label={`${t('companyName')}`}
          placeholder={`${t('enter')} ${firstLetterToLowerCaseInSentence(t('companyName'))}`}
          ariaLabel={`${t('companyName')}`}
          {...form.getInputProps('companyName')}
        />
        <Space h={'md'} />
        {/* {os === 'ios' || os === 'macos' ? (
          <>
             <NativeSelect
              label={`${t('industryField')}`}
              placeholder={`${t('select')} ${t('industryField')}`}
              className={styles.industry_select_native}
              data={industryOptionsNative}
              withBorder
              {...form.getInputProps('industryField')}
            />
          </>
        ) : (
          <>
            <Select
              label={`${t('industryField')}`}
              placeholder={`${t('select')} ${t('industryField')}`}
              className={styles.industry_select}
              data={industryOptions}
              withIcon
              {...form.getInputProps('industryField')}
            />
          </>
        )} */}

        {/* <Space h={'md'} /> */}
      </>
    </AuthFormHoc>
  );
};

export default OnboardingForm;
